import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Plus, Loader2, ChevronLeft, ChevronRight, Trash2, Flag, CalendarIcon, FileText, Download } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, isWithinInterval, addMonths, subMonths, getDay } from "date-fns";
import { it } from "date-fns/locale";

// Helper function to format date from YYYY-MM-DD to DD-MM-YYYY (Italian format)
const formatDateIT = (dateStr) => {
  if (!dateStr) return "";
  try {
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

export default function CalendarPage() {
  const { user, token, settings } = useAuth();
  const [calendarData, setCalendarData] = useState([]);
  const [events, setEvents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [newEvent, setNewEvent] = useState({
    tipo: "evento",
    titolo: "",
    colore: "#2563EB",
    descrizione: "",
    ora_inizio: "08:00",
    ora_fine: "10:00",
    dipendente_id: ""
  });

  const eventColors = [
    { value: "#2563EB", label: "Blu", bg: "bg-blue-500" },
    { value: "#10B981", label: "Verde", bg: "bg-emerald-500" },
    { value: "#F59E0B", label: "Arancione", bg: "bg-amber-500" },
    { value: "#EF4444", label: "Rosso", bg: "bg-red-500" },
    { value: "#8B5CF6", label: "Viola", bg: "bg-violet-500" },
    { value: "#EC4899", label: "Rosa", bg: "bg-pink-500" },
  ];

  // Get work week settings
  const giornoInizio = settings.giorno_inizio_settimana ?? 1;
  const giornoFine = settings.giorno_fine_settimana ?? 5;

  const isWorkingDay = (date) => {
    const dayOfWeek = getDay(date); // 0=Sunday, 1=Monday, etc.
    if (giornoInizio <= giornoFine) {
      return dayOfWeek >= giornoInizio && dayOfWeek <= giornoFine;
    } else {
      // Handles cases like Sat-Wed (6 to 3)
      return dayOfWeek >= giornoInizio || dayOfWeek <= giornoFine;
    }
  };

  useEffect(() => {
    fetchCalendarData();
    // Fetch employees for titolare or amministrativo with gestione_calendario permission
    if (user?.ruolo === "titolare" || (user?.ruolo === "amministrativo" && user?.permessi_admin?.gestione_calendario)) {
      fetchEmployees();
    }
  }, [token, currentMonth, user]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to get only dipendenti and amministrativi
      const filtered = response.data.filter(u => u.ruolo === "dipendente" || u.ruolo === "amministrativo");
      setEmployees(filtered);
    } catch (error) {
      console.error("Failed to fetch employees");
    }
  };

  const fetchCalendarData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const year = currentMonth.getFullYear();
      const [calRes, eventsRes] = await Promise.all([
        axios.get(`${API}/calendar?year=${year}`, { headers }),
        axios.get(`${API}/events`, { headers })
      ]);
      setCalendarData(calRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento del calendario");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    if (!dateRange.from) {
      toast.error("Seleziona almeno una data");
      return;
    }
    
    // Validate straordinario/malattia requires employee selection
    if ((newEvent.tipo === "straordinario" || newEvent.tipo === "malattia") && !newEvent.dipendente_id) {
      toast.error("Seleziona un dipendente");
      return;
    }
    
    setSubmitting(true);
    try {
      // Format dates for API
      const data_inizio = format(dateRange.from, "yyyy-MM-dd");
      const data_fine = dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : data_inizio;
      
      // Build event data based on type
      let eventData = {
        tipo: newEvent.tipo,
        data_inizio,
        data_fine,
        descrizione: newEvent.descrizione || ""
      };
      
      if (newEvent.tipo === "straordinario") {
        eventData.titolo = "Straordinario";
        eventData.colore = "#8B5CF6";  // Viola
        eventData.ora_inizio = newEvent.ora_inizio;
        eventData.ora_fine = newEvent.ora_fine;
        eventData.dipendente_id = newEvent.dipendente_id;
      } else if (newEvent.tipo === "malattia") {
        eventData.titolo = "Malattia";
        eventData.colore = "#F97316";  // Arancione
        eventData.dipendente_id = newEvent.dipendente_id;
      } else if (newEvent.tipo === "data_bloccata") {
        eventData.titolo = newEvent.titolo || "Data Bloccata";
        eventData.colore = "#DC2626";  // Rosso
      } else {
        eventData.titolo = newEvent.titolo;
        eventData.colore = newEvent.colore || "#2563EB";
      }
      
      await axios.post(`${API}/events`, eventData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Evento creato con successo!");
      setNewEventOpen(false);
      setNewEvent({ 
        tipo: "evento", 
        titolo: "", 
        colore: "#2563EB", 
        descrizione: "",
        ora_inizio: "08:00",
        ora_fine: "10:00",
        dipendente_id: ""
      });
      setDateRange({ from: undefined, to: undefined });
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione dell'evento");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Evento eliminato");
      fetchCalendarData();
    } catch (error) {
      toast.error("Errore nell'eliminazione dell'evento");
    }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      
      const response = await axios.get(`${API}/reports/monthly/${year}/${month}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const monthNames = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", 
                         "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
      link.download = `report_${monthNames[month]}_${year}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Report generato con successo!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella generazione del report");
    } finally {
      setGeneratingReport(false);
    }
  };

  const getEventsForDate = (date) => {
    return calendarData.filter((item) => {
      const start = parseISO(item.start);
      const end = parseISO(item.end);
      return isWithinInterval(date, { start, end }) || isSameDay(date, start) || isSameDay(date, end);
    });
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get day of week for first day - adjusted for Monday start (0 = Monday, 6 = Sunday)
  const startDayRaw = monthStart.getDay(); // 0 = Sunday
  const startDay = startDayRaw === 0 ? 6 : startDayRaw - 1; // Convert to Monday-based (0 = Monday)
  const emptyDays = Array(startDay).fill(null);

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  // Separate holidays and other events for selected date
  const holidaysForDate = selectedDateEvents.filter(e => e.type === "holiday" || e.type === "local_holiday");
  const otherEventsForDate = selectedDateEvents.filter(e => e.type !== "holiday" && e.type !== "local_holiday");

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-3 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="calendar-title">
              Calendario
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Report button for titolare and amministrativo */}
            <div className={(user?.ruolo === "titolare" || user?.ruolo === "amministrativo") ? "" : "hidden"}>
              <Button 
                variant="outline" 
                onClick={handleGenerateReport}
                disabled={generatingReport}
                className="btn-theme-light btn-press"
                data-testid="generate-report-btn"
              >
                {generatingReport ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                Genera report mensile
              </Button>
            </div>
            {/* Show button for titolare or amministrativo with gestione_calendario permission */}
            <div className={(user?.ruolo === "titolare" || (user?.ruolo === "amministrativo" && user?.permessi_admin?.gestione_calendario)) ? "" : "hidden"}>
              <Dialog open={newEventOpen} onOpenChange={(open) => {
                setNewEventOpen(open);
                if (open) {
                  // Reset form when opening
                  setDateRange({ from: undefined, to: undefined });
                  // For amministrativo, default to straordinario (they can only create straordinario/malattia)
                  const defaultTipo = user?.ruolo === "amministrativo" ? "straordinario" : "evento";
                  setNewEvent({ 
                    tipo: defaultTipo, 
                    titolo: "", 
                    colore: "#2563EB", 
                    descrizione: "",
                    ora_inizio: "08:00",
                    ora_fine: "10:00",
                    dipendente_id: ""
                  });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-theme btn-press" data-testid="new-event-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    {user?.ruolo === "amministrativo" ? "Aggiungi Straordinario/Malattia" : "Aggiungi Evento"}
                  </Button>
                </DialogTrigger>

                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {user?.ruolo === "amministrativo" ? "Nuovo Straordinario/Malattia" : "Nuovo Evento"}
                    </DialogTitle>
                    <DialogDescription>
                      {user?.ruolo === "amministrativo" 
                        ? "Aggiungi straordinario o malattia al calendario." 
                        : "Crea un nuovo evento nel calendario condiviso."}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateEvent} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Tipo evento</Label>
                    <Select value={newEvent.tipo} onValueChange={(v) => setNewEvent({ ...newEvent, tipo: v, dipendente_id: "" })}>
                      <SelectTrigger data-testid="event-tipo-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="straordinario">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-violet-500"></div>
                            Straordinario
                          </div>
                        </SelectItem>
                        <SelectItem value="malattia">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                            Malattia
                          </div>
                        </SelectItem>
                        {/* Only show these options for titolare */}
                        {user?.ruolo === "titolare" && (
                          <>
                            <SelectItem value="ferie_attivita">Ferie Attività</SelectItem>
                            <SelectItem value="data_bloccata">Data Bloccata</SelectItem>
                            <SelectItem value="evento">Evento Personalizzato</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    {newEvent.tipo === "data_bloccata" && (
                      <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200 mt-2">
                        ⚠️ <strong>Attenzione:</strong> I dipendenti non potranno richiedere ferie o permessi nelle date bloccate.
                      </p>
                    )}
                    {newEvent.tipo === "straordinario" && (
                      <p className="text-sm text-violet-600 bg-violet-50 p-3 rounded-lg border border-violet-200 mt-2">
                        🕐 <strong>Straordinario:</strong> Seleziona il dipendente e l'orario di lavoro straordinario.
                      </p>
                    )}
                    {newEvent.tipo === "malattia" && (
                      <p className="text-sm text-orange-600 bg-orange-50 p-3 rounded-lg border border-orange-200 mt-2">
                        🏥 <strong>Malattia:</strong> Seleziona il dipendente e le date di assenza per malattia.
                      </p>
                    )}
                  </div>

                  {/* Employee selection for straordinario/malattia */}
                  {(newEvent.tipo === "straordinario" || newEvent.tipo === "malattia") && (
                    <div className="space-y-2">
                      <Label>Dipendente *</Label>
                      <Select 
                        value={newEvent.dipendente_id} 
                        onValueChange={(v) => setNewEvent({ ...newEvent, dipendente_id: v })}
                      >
                        <SelectTrigger data-testid="event-dipendente-select">
                          <SelectValue placeholder="Seleziona dipendente..." />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map((emp) => (
                            <SelectItem key={emp.id} value={emp.id}>
                              {emp.nome} {emp.cognome} 
                              <span className="text-slate-400 ml-2 text-xs capitalize">({emp.ruolo})</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Time selection for straordinario */}
                  {newEvent.tipo === "straordinario" && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Dalle ore *</Label>
                        <Input
                          type="time"
                          value={newEvent.ora_inizio}
                          onChange={(e) => setNewEvent({ ...newEvent, ora_inizio: e.target.value })}
                          required
                          data-testid="event-ora-inizio"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Alle ore *</Label>
                        <Input
                          type="time"
                          value={newEvent.ora_fine}
                          onChange={(e) => setNewEvent({ ...newEvent, ora_fine: e.target.value })}
                          required
                          data-testid="event-ora-fine"
                        />
                      </div>
                    </div>
                  )}

                  {/* Title field - only for non-straordinario/malattia */}
                  {newEvent.tipo !== "straordinario" && newEvent.tipo !== "malattia" && (
                    <div className="space-y-2">
                      <Label>
                        Titolo {newEvent.tipo === "data_bloccata" && <span className="text-slate-400 font-normal">(Opzionale)</span>}
                      </Label>
                      <Input
                        placeholder={newEvent.tipo === "data_bloccata" ? "Es: Inventario (lascia vuoto per 'Data Bloccata')" : "Es: Chiusura estiva"}
                        value={newEvent.titolo}
                        onChange={(e) => setNewEvent({ ...newEvent, titolo: e.target.value })}
                        required={newEvent.tipo !== "data_bloccata"}
                        data-testid="event-titolo"
                      />
                    </div>
                  )}
                  
                  {/* Date Range Picker */}
                  <div className="space-y-2">
                    <Label>Seleziona date</Label>
                    <p className="text-xs text-slate-500 mb-2">
                      Clicca su una data per selezionarla, poi clicca su un'altra data per selezionare un intervallo
                    </p>
                    <div className="border rounded-lg p-3 bg-slate-50">
                      <Calendar
                        mode="range"
                        selected={dateRange}
                        onSelect={setDateRange}
                        defaultMonth={new Date()}
                        locale={it}
                        numberOfMonths={1}
                        weekStartsOn={1}
                        className="mx-auto"
                        data-testid="event-date-picker"
                      />
                    </div>
                    
                    {/* Show hint when only start date is selected */}
                    {dateRange.from && !dateRange.to && (
                      <div className="flex items-center gap-2 text-sm bg-amber-50 text-amber-700 p-3 rounded-lg border border-amber-200 animate-pulse">
                        <CalendarIcon className="h-4 w-4 shrink-0" />
                        <span><strong>Adesso seleziona la data di fine evento</strong></span>
                      </div>
                    )}
                    
                    {/* Show selected range */}
                    {dateRange.from && dateRange.to && (
                      <div className="flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 p-2 rounded-lg border border-emerald-200">
                        <CalendarIcon className="h-4 w-4 text-emerald-500" />
                        <span>
                          {format(dateRange.from, "dd MMM yyyy", { locale: it })}
                          {dateRange.to.getTime() !== dateRange.from.getTime() && (
                            <> → {format(dateRange.to, "dd MMM yyyy", { locale: it })}</>
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className={`space-y-2 ${newEvent.tipo === "data_bloccata" || newEvent.tipo === "straordinario" || newEvent.tipo === "malattia" ? "hidden" : ""}`}>
                    <Label>Colore</Label>
                    <div className="flex gap-2">
                      {eventColors.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          onClick={() => setNewEvent({ ...newEvent, colore: color.value })}
                          className={`h-8 w-8 rounded-full ${color.bg} transition-transform ${newEvent.colore === color.value ? "ring-2 ring-offset-2 ring-slate-900 scale-110" : "hover:scale-105"}`}
                          title={color.label}
                        />
                      ))}
                    </div>
                  </div>
                  {newEvent.tipo === "data_bloccata" && (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <div className="h-4 w-4 rounded-full bg-red-500"></div>
                      <span>Le date bloccate appaiono in rosso nel calendario</span>
                    </div>
                  )}
                  {newEvent.tipo === "straordinario" && (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <div className="h-4 w-4 rounded-full bg-violet-500"></div>
                      <span>Lo straordinario appare in viola nel calendario</span>
                    </div>
                  )}
                  {newEvent.tipo === "malattia" && (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <div className="h-4 w-4 rounded-full bg-orange-500"></div>
                      <span>La malattia appare in arancione nel calendario</span>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Descrizione (opzionale)</Label>
                    <Textarea
                      placeholder="Aggiungi dettagli..."
                      value={newEvent.descrizione}
                      onChange={(e) => setNewEvent({ ...newEvent, descrizione: e.target.value })}
                      data-testid="event-descrizione"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-theme" disabled={submitting} data-testid="submit-event-btn">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Crea Evento
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          </div>
        </div>

        {/* Legend - Visible to all users */}
        <Card className="border-slate-200">
          <CardContent className="py-2">
            <div className="flex flex-wrap gap-3 sm:gap-5 text-[11px] sm:text-xs">
              <div className="flex items-center gap-2">
                <Flag className="h-3.5 w-3.5 text-red-500" />
                <span className="text-slate-600">Festività nazionali</span>
              </div>
              <div className="flex items-center gap-2">
                <Flag className="h-3.5 w-3.5 text-violet-500" />
                <span className="text-slate-600">Festività locali</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-emerald-500"></div>
                <span className="text-slate-600">Ferie approvate</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                <span className="text-slate-600">Permessi approvati</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-violet-500"></div>
                <span className="text-slate-600">Straordinario</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-orange-500"></div>
                <span className="text-slate-600">Malattia</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                <span className="text-slate-600">Eventi</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded bg-slate-200"></div>
                <span className="text-slate-600">Giorni non lavorativi</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Calendar */}
          <Card className="lg:col-span-2 border-slate-200">
            <CardHeader className="pb-2 sm:pb-4 px-3 sm:px-6">
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  data-testid="prev-month-btn"
                  className="h-10 w-10 sm:h-12 sm:w-12 border-2 border-theme-500 bg-theme-50 hover:bg-theme-100 text-theme-600 hover:text-theme-700 transition-all"
                >
                  <ChevronLeft className="h-6 w-6 sm:h-7 sm:w-7" />
                </Button>
                <h2 className="text-base sm:text-xl font-semibold text-slate-900 capitalize" data-testid="current-month">
                  {format(currentMonth, "MMMM yyyy", { locale: it })}
                </h2>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  data-testid="next-month-btn"
                  className="h-10 w-10 sm:h-12 sm:w-12 border-2 border-theme-500 bg-theme-50 hover:bg-theme-100 text-theme-600 hover:text-theme-700 transition-all"
                >
                  <ChevronRight className="h-6 w-6 sm:h-7 sm:w-7" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-2 sm:px-6 pb-3 sm:pb-6">
              {/* Week days header - Monday to Sunday */}
              <div className="grid grid-cols-7 gap-0.5 mb-1 sm:mb-2">
                {["L", "M", "M", "G", "V", "S", "D"].map((day, idx) => {
                  // idx: 0=Mon, 1=Tue, ..., 6=Sun -> convert to JS day (0=Sun, 1=Mon, ...)
                  const jsDay = idx === 6 ? 0 : idx + 1;
                  const refDate = new Date(2024, 0, 1 + jsDay); // Jan 1, 2024 was Monday
                  const isWorkDay = isWorkingDay(refDate);
                  const fullDays = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
                  return (
                    <div 
                      key={`${day}-${idx}`} 
                      className={`text-center text-[10px] sm:text-sm font-medium py-1 ${isWorkDay ? "text-slate-900" : "text-slate-400"}`}
                    >
                      <span className="hidden sm:inline">{fullDays[idx]}</span>
                      <span className="sm:hidden">{day}</span>
                    </div>
                  );
                })}
              </div>
              {/* Calendar grid - compact to fit viewport */}
              <div className="grid grid-cols-7 gap-0.5">
                {emptyDays.map((_, i) => (
                  <div key={`empty-${i}`} className="h-[calc((100vh-420px)/6)] min-h-[36px] max-h-[72px]" />
                ))}
                {daysInMonth.map((day) => {
                  const dayEvents = getEventsForDate(day);
                  const isSelected = selectedDate && isSameDay(day, selectedDate);
                  const isToday = isSameDay(day, new Date());
                  const isWorkDay = isWorkingDay(day);
                  const hasNationalHoliday = dayEvents.some(e => e.type === "holiday");
                  const hasLocalHoliday = dayEvents.some(e => e.type === "local_holiday");
                  const hasAnyHoliday = hasNationalHoliday || hasLocalHoliday;
                  const nonHolidayEvents = dayEvents.filter(e => e.type !== "holiday" && e.type !== "local_holiday");
                  
                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => setSelectedDate(day)}
                      className={`h-[calc((100vh-420px)/6)] min-h-[36px] max-h-[72px] p-0.5 sm:p-1 rounded-md sm:rounded-lg transition-colors relative flex flex-col ${
                        isSelected
                          ? "bg-theme-600 text-white ring-2 ring-theme-400"
                          : isToday
                          ? "bg-theme-100 text-theme-900 ring-1 ring-theme-300"
                          : hasNationalHoliday
                          ? "bg-red-50 text-red-900"
                          : hasLocalHoliday
                          ? "bg-violet-50 text-violet-900"
                          : !isWorkDay
                          ? "bg-slate-100 text-slate-400"
                          : "hover:bg-slate-50"
                      }`}
                      data-testid={`day-${format(day, "yyyy-MM-dd")}`}
                    >
                      <span className={`text-xs sm:text-sm font-semibold ${hasNationalHoliday && !isSelected ? "text-red-600" : hasLocalHoliday && !isSelected ? "text-violet-600" : ""}`}>
                        {format(day, "d")}
                      </span>
                      {hasAnyHoliday && !isSelected && (
                        <Flag className={`absolute top-0.5 right-0.5 sm:top-1 sm:right-1 h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 ${hasNationalHoliday ? "text-red-500" : "text-violet-500"}`} />
                      )}
                      {/* Event indicators - more visible */}
                      {nonHolidayEvents.length > 0 && (
                        <div className="flex-1 flex flex-col justify-end gap-[2px] mt-0.5 overflow-hidden w-full">
                          {nonHolidayEvents.slice(0, 2).map((evt, i) => (
                            <div
                              key={i}
                              className="h-[14px] sm:h-[18px] w-full rounded-sm flex items-center overflow-hidden"
                              style={{ backgroundColor: evt.color }}
                              title={evt.title}
                            >
                              <span className="text-[9px] sm:text-[11px] text-white font-bold px-1 sm:px-1.5 truncate leading-none">
                                {evt.title}
                              </span>
                            </div>
                          ))}
                          {nonHolidayEvents.length > 2 && (
                            <div className={`text-[8px] sm:text-[9px] font-bold text-center ${isSelected ? "text-white/80" : "text-slate-500"}`}>
                              +{nonHolidayEvents.length - 2}
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Selected Date Events */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">
                {selectedDate
                  ? format(selectedDate, "d MMMM yyyy", { locale: it })
                  : "Seleziona una data"}
              </CardTitle>
              <CardDescription>
                {selectedDate
                  ? `${selectedDateEvents.length} eventi`
                  : "Clicca su un giorno per vedere i dettagli"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedDate ? (
                <>
                  {/* Show if it's a non-working day */}
                  {!isWorkingDay(selectedDate) && (
                    <div className="mb-4 p-3 bg-slate-100 rounded-lg">
                      <p className="text-sm text-slate-600 font-medium">Giorno non lavorativo</p>
                    </div>
                  )}
                  
                  {/* Holidays section */}
                  {holidaysForDate.length > 0 && (
                    <div className="mb-4 space-y-2">
                      {holidaysForDate.map((holiday) => (
                        <div
                          key={holiday.id}
                          className={`p-3 rounded-lg flex items-center gap-2 ${
                            holiday.type === "holiday" 
                              ? "bg-red-50 border border-red-100" 
                              : "bg-violet-50 border border-violet-100"
                          }`}
                          data-testid={`holiday-${holiday.id}`}
                        >
                          <Flag className={`h-4 w-4 flex-shrink-0 ${holiday.type === "holiday" ? "text-red-500" : "text-violet-500"}`} />
                          <div>
                            <p className={`font-medium text-sm ${holiday.type === "holiday" ? "text-red-900" : "text-violet-900"}`}>
                              {holiday.title}
                            </p>
                            <p className={`text-xs ${holiday.type === "holiday" ? "text-red-600" : "text-violet-600"}`}>
                              {holiday.type === "holiday" ? "Festività nazionale" : "Festività locale"}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Other events */}
                  {otherEventsForDate.length === 0 && holidaysForDate.length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-8">Nessun evento in questa data</p>
                  ) : otherEventsForDate.length > 0 ? (
                    <div className="space-y-3">
                      {otherEventsForDate.map((event) => (
                        <div
                          key={event.id}
                          className="p-3 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors"
                          style={{ borderLeftWidth: 4, borderLeftColor: event.color }}
                          data-testid={`event-detail-${event.id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium text-slate-900 text-sm">{event.title}</p>
                              <p className="text-xs text-slate-500 mt-1">
                                {formatDateIT(event.start)} → {formatDateIT(event.end)}
                              </p>
                              {event.meta?.descrizione && (
                                <p className="text-xs text-slate-400 mt-1">{event.meta.descrizione}</p>
                              )}
                            </div>
                            {user?.ruolo === "titolare" && event.type === "event" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-slate-400 hover:text-red-500"
                                onClick={() => handleDeleteEvent(event.id)}
                                data-testid={`delete-event-${event.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="h-16 w-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Plus className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="text-slate-500 text-sm">Seleziona una data dal calendario</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
